import { supabase } from '@/lib/supabase';
import type { OrderStatus } from '@/types';
import { sendOrderNotification } from './seller-helpers';

export interface OrderUpdateData {
  status: OrderStatus;
  reason?: string;
  notes?: string;
}

/**
 * 주문 상태를 업데이트합니다
 */
export async function updateOrderStatus(orderId: string, updateData: OrderUpdateData): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    const updatePayload: any = {
      status: updateData.status,
      updated_at: new Date().toISOString()
    };

    // 상태별 추가 필드 처리
    if (updateData.reason) {
      updatePayload.cancellation_reason = updateData.reason;
    }

    if (updateData.notes) {
      updatePayload.notes = updateData.notes;
    }

    const { error } = await supabase
      .from('orders')
      .update(updatePayload)
      .eq('id', orderId);

    if (error) {
      console.error('Order status update error:', error);
      throw error;
    }

    // CANCELLED 상태인 경우 payments 테이블도 함께 업데이트
    if (updateData.status === 'CANCELLED') {
      try {
        const { error: paymentError } = await supabase
          .from('payments')
          .update({ 
            status: 'cancelled',
            updated_at: new Date().toISOString()
          })
          .eq('order_id', orderId);

        if (paymentError) {
          console.error('Payment status update error:', paymentError);
          // payments 테이블 업데이트 실패는 치명적이지 않으므로 로그만 남김
        }
      } catch (paymentUpdateError) {
        console.error('Payment update failed:', paymentUpdateError);
        // 결제 상태 업데이트 실패 시에도 주문 취소는 계속 진행
      }
    }

    // 카카오톡 알림 발송 (주문 수락, 준비완료 시)
    // 취소는 rejectOrder 함수에서 처리하므로 제외
    if (updateData.status === 'ACCEPTED' || updateData.status === 'READY') {
      await sendOrderNotification(orderId, updateData.status);
    } else if (updateData.status === 'CANCELLED' && !updateData.reason?.includes('판매자 거부')) {
      // 일반 취소만 처리 (판매자 거부가 아닌 경우)
      await sendOrderNotification(orderId, updateData.status);
    }

    return { success: true };
  } catch (error) {
    console.error('Order status update error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '주문 상태 업데이트에 실패했습니다'
    };
  }
}

/**
 * 주문을 수락합니다
 */
export async function acceptOrder(orderId: string, notes?: string): Promise<{
  success: boolean;
  error?: string;
}> {
  return updateOrderStatus(orderId, {
    status: 'ACCEPTED',
    notes
  });
}

/**
 * 주문을 거절합니다
 */
export async function rejectOrder(orderId: string, reason: string): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    // DB 직접 업데이트
    const { error } = await supabase
      .from('orders')
      .update({
        status: 'CANCELLED',
        cancellation_reason: `판매자 거부: ${reason}`,
        cancelled_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', orderId);

    if (error) {
      console.error('Order reject error:', error);
      throw error;
    }

    // 환불 처리 - Edge Function 호출
    try {
      const { data: refundData, error: refundError } = await supabase.functions.invoke('process-refund', {
        body: {
          orderId,
          reason: `판매자 거부: ${reason}`,
          refundType: 'full'
        }
      });

      if (refundError) {
        console.error('환불 처리 실패:', refundError);
        // 환불 실패해도 주문 취소는 유지, 로그만 남김
      } else {
        console.log('환불 처리 성공:', refundData);
      }
    } catch (refundErr) {
      console.error('환불 Edge Function 호출 실패:', refundErr);
      // 환불 실패해도 계속 진행
    }

    // payments 테이블 업데이트
    try {
      const { error: paymentError } = await supabase
        .from('payments')
        .update({ 
          status: 'cancelled',
          updated_at: new Date().toISOString()
        })
        .eq('order_id', orderId);

      if (paymentError) {
        console.error('Payment status update error:', paymentError);
      }
    } catch (paymentUpdateError) {
      console.error('Payment update failed:', paymentUpdateError);
    }

    // 판매자 거부 알림 발송 (order_rejected 타입)
    await sendOrderNotification(orderId, 'CANCELLED', true);

    return { success: true };
  } catch (error) {
    console.error('Order reject error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '주문 거부에 실패했습니다'
    };
  }
}

/**
 * 주문을 준비 완료 상태로 변경합니다
 */
export async function markOrderReady(orderId: string): Promise<{
  success: boolean;
  error?: string;
}> {
  return updateOrderStatus(orderId, {
    status: 'READY'
  });
}

/**
 * 주문을 완료 처리합니다
 */
export async function completeOrder(orderId: string): Promise<{
  success: boolean;
  error?: string;
}> {
  return updateOrderStatus(orderId, {
    status: 'COMPLETED'
  });
}

/**
 * 주문을 노쇼 처리합니다
 */
export async function markOrderNoShow(orderId: string, reason?: string): Promise<{
  success: boolean;
  error?: string;
}> {
  return updateOrderStatus(orderId, {
    status: 'NOSHOW',
    reason
  });
}

/**
 * 주문을 취소합니다
 */
export async function cancelOrder(orderId: string, reason: string): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    // DB 상태 업데이트
    const { error: updateError } = await supabase
      .from('orders')
      .update({
        status: 'CANCELLED',
        cancellation_reason: reason,
        cancelled_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', orderId);

    if (updateError) {
      console.error('Order cancel DB update error:', updateError);
      throw updateError;
    }

    // 환불 처리 - Edge Function 호출
    try {
      const { data: refundData, error: refundError } = await supabase.functions.invoke('process-refund', {
        body: {
          orderId,
          reason: reason,
          refundType: 'full'
        }
      });

      if (refundError) {
        console.error('환불 처리 실패:', refundError);
        // 환불 실패해도 주문 취소는 유지, 로그만 남김
      } else {
        console.log('환불 처리 성공:', refundData);
      }
    } catch (refundErr) {
      console.error('환불 Edge Function 호출 실패:', refundErr);
      // 환불 실패해도 계속 진행
    }

    // payments 테이블 업데이트
    try {
      const { error: paymentError } = await supabase
        .from('payments')
        .update({ 
          status: 'cancelled',
          updated_at: new Date().toISOString()
        })
        .eq('order_id', orderId);

      if (paymentError) {
        console.error('Payment status update error:', paymentError);
      }
    } catch (paymentUpdateError) {
      console.error('Payment update failed:', paymentUpdateError);
    }

    // 일반 취소 알림 발송
    await sendOrderNotification(orderId, 'CANCELLED', false);

    return { success: true };
  } catch (error) {
    console.error('Order cancel error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '주문 취소에 실패했습니다'
    };
  }
}

/**
 * 마감할인 템플릿 비활성화 시 당일 관련 주문을 취소합니다
 */
export async function cancelDeadlineOrdersByProducts(
  restaurantId: string,
  productIds: string[],
  reason: string = '마감할인 비활성화로 인한 취소'
): Promise<{ success: boolean; cancelled: string[]; error?: string }> {
  const cancelled: string[] = [];

  try {
    if (productIds.length === 0) return { success: true, cancelled };

    const today = new Date().toISOString().split('T')[0];
    const start = `${today}T00:00:00Z`;
    const end = `${today}T23:59:59Z`;

    const { data: orders, error } = await supabase
      .from('orders')
      .select('id, order_items!inner(product_id)')
      .eq('restaurant_id', restaurantId)
      .eq('order_type', 'DEADLINE_DISCOUNT')
      .in('status', ['PENDING', 'ACCEPTED', 'READY'])
      .gte('created_at', start)
      .lte('created_at', end)
      .in('order_items.product_id', productIds);

    if (error) {
      console.error('Fetch deadline orders for cancel error:', error);
      throw error;
    }

    const uniqueOrderIds = Array.from(new Set((orders || []).map(o => o.id)));

    for (const orderId of uniqueOrderIds) {
      const result = await cancelOrder(orderId, reason);
      if (result.success) {
        cancelled.push(orderId);
      }
    }

    return { success: true, cancelled };
  } catch (err) {
    console.error('Cancel deadline orders error:', err);
    return {
      success: false,
      cancelled,
      error: err instanceof Error ? err.message : '마감할인 주문 취소에 실패했습니다'
    };
  }
}
